import { computed, action, observable } from 'mobx';
import { ProductSortingService } from './ProductSortingService';
import { FilterService } from './FilterService';
import { ProductRepository } from '../../repositories/ProductRepository';
import { IQueryTypeFragments, QueryFragments } from '../../../api/QueryFragments';
import { IFeatures } from '../../constants/Features';
import { Product } from '../entities/Product';
import { IItem } from '~api/interfaces/IItem';
import { getItemsFromParamsById } from '~api/utils/Filters';
import { IProductParams } from '~api/interfaces/IParams';
import { ItemTypes } from '~constants/ItemTypes';
import {
    IContentItem,
    IProductGridConfig
} from '../../../exported/product-grid-wrapper/ProductGridWrapper';
import {
    IProductDataParams,
    IProductDataOptions,
    SortDirection
} from '~api/interfaces/IProductDataOptions';
import { IItemByType, splitItemByType } from '~api/utils/SplitItemByType';

export class ProductGridService {
    public filterService: FilterService;
    public productSortingService: ProductSortingService;
    public productRepository: ProductRepository;
    @observable private content: IContentItem[] = [];
    @observable private queryFragments: QueryFragments;

    constructor(
        filterService: FilterService,
        productSortingService: ProductSortingService,
        productRepository: ProductRepository,
        queryFragments: QueryFragments
    ) {
        this.filterService = filterService;
        this.productSortingService = productSortingService;
        this.productRepository = productRepository;
        this.queryFragments = queryFragments;
    }

    @action
    public setInlineContent(content: IContentItem[]) {
        this.content = content;
    }

    @computed
    public get inlineContent() {
        return this.content;
    }

    public filterSkus(productsResponse: Product[], items: IItem[]): Product[] {
        return items.reduce((products: Product[], item: IItem) => {
            const { params, type } = item;

            const isSku = type === ItemTypes.sku;
            const isProduct = type === ItemTypes.product;

            const { id, skusToDisplay } = <IProductParams>params;

            const product = isSku
                ? productsResponse.find((productResponse) =>
                      productResponse.initialSkus.find((sku) => sku.skuId === id)
                  )
                : productsResponse.find((productResponse) => productResponse.productId === id);
            if (!!product) {
                const productClone = Product.clone(product);
                if ((isProduct && skusToDisplay) || isSku) {
                    const filterMethod = isSku
                        ? ({ skuId }: { skuId: string }) => skuId === id
                        : ({ skuId }: { skuId: string }) => (skusToDisplay || []).includes(skuId);
                    productClone.initialSkus = productClone.initialSkus.filter(filterMethod);
                }
                if (productClone.initialSkus && productClone.initialSkus.length > 0) {
                    products.push(productClone);
                }
            }

            return products;
        }, []);
    }

    public getProductData = async (
        options: IProductDataOptions,
        queryFragments?: IQueryTypeFragments
    ) => {
        const products = (await this.productRepository.getProductData(
            options,
            queryFragments
        )) as Product[];
        if (options.gridItems && options.gridItems?.length > 0) {
            return this.filterSkus(products, options.gridItems);
        }

        return products;
    };

    // @todo remove on MPPFIND-4454
    public getProductsByGridConfig = async (config: IProductGridConfig): Promise<Product[]> => {
        const {
            items: propsItems = [],
            productTags = [],
            content = [],
            paramsById,
            featuredProductId,
            features
        } = config;

        this.queryFragments.setFeatures(<IFeatures>features);
        const queryFragments = this.queryFragments.queryFragments;
        let items: IItem[] = propsItems;
        if (paramsById) {
            items = getItemsFromParamsById(paramsById, featuredProductId);
        }

        this.setInlineContent(content);

        if (items.length > 0) {
            const productsResponse = await this.productRepository.getProductsByItems(items, {
                queryFragments
            });

            return this.filterSkus(productsResponse, items);
        } else if (productTags.length > 0) {
            return this.productRepository.getProductsByTag(productTags, queryFragments);
        }

        return <Product[]>[];
    };

    public getProductGridDataParams = (config: IProductGridConfig): IProductDataParams => {
        const {
            items: propsItems = [],
            productTags = [],
            content = [],
            paramsById,
            features
        } = config;

        this.queryFragments.setFeatures(<IFeatures>features);
        const queryFragments = this.queryFragments.queryFragments;

        this.setInlineContent(content);

        let productIdsDataParams;
        let tagsDataParams;
        let tagsDataDisplayOrder;
        let skusDataParams;

        if (productTags.length > 0) {
            const productTagsParams = this.productRepository.getProductTagsParams(productTags);
            tagsDataParams = {
                ids: productTagsParams.productTagIds.length
                    ? productTagsParams.productTagIds
                    : undefined,
                keys: productTagsParams.productTagsKeys.length
                    ? productTagsParams.productTagsKeys
                    : undefined,
                includeInnerHits: false
            };

            const tagsSortByCriteria = {
                tags: {
                    productDisplayOrder: SortDirection.ASCENDING
                }
            };
            tagsDataDisplayOrder =
                tagsDataParams.ids || tagsDataParams.keys ? tagsSortByCriteria : undefined;
        }

        let items: IItem[] = [];
        let productIdsFromParams = paramsById?.productIds;
        let skuIdsFromParams = paramsById?.skuIds;
        if (propsItems.length > 0) {
            items = propsItems;
            const { sku: skus, product: products }: IItemByType = splitItemByType(items);

            productIdsFromParams = this.productRepository.getProductIdsFromParams(products);
            skuIdsFromParams = this.productRepository.getSkuIdsFromParams(skus);

            productIdsDataParams =
                productIdsFromParams && productIdsFromParams.length > 0
                    ? productIdsFromParams
                    : undefined;
            skusDataParams =
                skuIdsFromParams && skuIdsFromParams.length > 0
                    ? {
                          skuIds: skuIdsFromParams,
                          includeInnerHits: true
                      }
                    : undefined;
        }

        return {
            options: {
                filterBy: {
                    tags: tagsDataParams,
                    skus: skusDataParams,
                    productIds: productIdsDataParams
                },
                sortBy: tagsDataDisplayOrder,
                productGridConfig: config,
                gridItems: items,
                updateInventories: true
            },
            queryFragments
        };
    };
}
